/**
 * SAT Circle of Life™ · Assessment Page (STEP 1)
 * Reuse: `Lebensrad.jsx` Slider-Muster · `CircleOfLifeLanding.jsx`-CTA-Look.
 * Kein neuer Report — nur R/D/I-Erhebung + Envelope-Release.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem("sat_token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function CircleAssessment() {
  const [dimensions, setDimensions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [ratings, setRatings] = useState({}); // key → {reality, desire, importance}
  const [envelope, setEnvelope] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/circle/registry`);
        setDimensions(r.data.dimensions);
        // init defaults
        const init = {};
        r.data.dimensions.forEach(d => { init[d.key] = { reality: 5, desire: 8, importance: 5 }; });
        setRatings(init);
        // check current session
        const s = await axios.get(`${API}/api/circle/session/current`, { headers: authHeaders() });
        if (s.data.session_id) {
          setSessionId(s.data.session_id);
          s.data.ratings.forEach(r => {
            init[r.dimension_key] = { reality: r.reality, desire: r.desire, importance: r.importance };
          });
          setRatings({ ...init });
        }
      } catch (e) {
        setError(e?.response?.data?.detail?.message || "Circle ist derzeit nicht verfügbar.");
      }
    })();
  }, []);

  async function startSession() {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/circle/session`, {}, { headers: authHeaders() });
      setSessionId(r.data.session_id);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
    setLoading(false);
  }

  function updateRating(key, field, value) {
    setRatings(prev => ({ ...prev, [key]: { ...prev[key], [field]: Number(value) } }));
  }

  async function saveAndRelease() {
    setLoading(true);
    try {
      await axios.post(`${API}/api/circle/ratings`, {
        session_id: sessionId,
        ratings: dimensions.map(d => ({ dimension_key: d.key, ...ratings[d.key] })),
      }, { headers: authHeaders() });
      const r = await axios.post(`${API}/api/circle/session/${sessionId}/release`, {}, { headers: authHeaders() });
      setEnvelope(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
    setLoading(false);
  }

  const avg = useMemo(() => {
    const vals = Object.values(ratings);
    if (!vals.length) return 0;
    return (vals.reduce((s, r) => s + (r.reality || 0), 0) / vals.length).toFixed(1);
  }, [ratings]);

  return (
    <div className="min-h-screen bg-[#08102A] text-slate-100" data-testid="circle-assessment-page">
      <div className="mx-auto max-w-4xl px-6 py-14">
        <p className="mb-2 text-xs uppercase tracking-widest text-[#D4AF37]">Circle of Life™</p>
        <h1 className="mb-6 text-4xl font-light text-[#F6E7B8] sm:text-5xl">
          Die Landkarte deines Lebens
        </h1>
        <p className="mb-10 max-w-2xl text-slate-300">
          Bewerte jede der 12 Lebensdimensionen aus drei Perspektiven:
          <span className="text-[#D4AF37]"> Reality</span> (wo du heute stehst),
          <span className="text-[#D4AF37]"> Desire</span> (wo du sein möchtest),
          <span className="text-[#D4AF37]"> Importance</span> (wie wichtig dir dieser Bereich ist).
        </p>

        {error && (
          <div className="mb-6 rounded border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
               data-testid="circle-error">{error}</div>
        )}

        {envelope && (
          <div className="mb-8 rounded-2xl border border-[#D4AF37]/30 bg-slate-900/40 p-6"
               data-testid="circle-result">
            <p className="text-sm uppercase tracking-widest text-[#D4AF37]">Dein Life Alignment</p>
            <p className="mt-2 text-6xl font-light text-white">{envelope.overall_alignment_score}<span className="text-2xl text-slate-500">/100</span></p>
            <p className="mt-4 text-sm text-slate-300">Größter Entwicklungsraum: <span className="text-[#D4AF37]">{envelope.top_gap_dimensions?.join(" · ")}</span></p>
            <p className="mt-1 text-sm text-slate-300">Was bereits trägt: <span className="text-[#D4AF37]">{envelope.strongest_dimensions?.join(" · ")}</span></p>
            <p className="mt-4 text-xs text-slate-500">Envelope: {envelope.envelope_version} · Hash: {envelope.export_hash?.slice(0, 12)}…</p>
          </div>
        )}

        {!sessionId ? (
          <button onClick={startSession} disabled={loading}
                  data-testid="circle-start-btn"
                  className="rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-medium text-[#08102A] hover:bg-[#F6E7B8]">
            {loading ? "…" : "Circle of Life starten"}
          </button>
        ) : !envelope && (
          <>
            <div className="mb-6 rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3 text-sm">
              Durchschnittliche Reality: <span className="text-[#D4AF37]">{avg}</span>/10
            </div>
            <div className="space-y-4">
              {dimensions.map(d => (
                <div key={d.key} data-testid={`circle-dim-${d.key}`}
                     className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-white">{d.label}</p>
                  </div>
                  {["reality", "desire", "importance"].map(field => (
                    <div key={field} className="mb-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="uppercase tracking-widest text-slate-400">{field}</span>
                        <span className="text-[#D4AF37]">{ratings[d.key]?.[field] ?? 5}</span>
                      </div>
                      <input type="range" min="0" max="10"
                             value={ratings[d.key]?.[field] ?? 5}
                             onChange={e => updateRating(d.key, field, e.target.value)}
                             data-testid={`circle-slider-${d.key}-${field}`}
                             className="w-full accent-[#D4AF37]" />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <button onClick={saveAndRelease} disabled={loading}
                    data-testid="circle-release-btn"
                    className="mt-8 rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-medium text-[#08102A] hover:bg-[#F6E7B8] disabled:opacity-50">
              {loading ? "Speichere…" : "Auswertung freigeben"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
